import React from "react";
import PropTypes from "prop-types";
import useTheme from "@mui/material/styles/useTheme";
import { Stack, Typography, Divider, Fade, Box } from "@mui/material";
import { Link, MainLayout, Section } from "@arp/react-ndbx";
import { URLS } from "../../config/const/enums";
import useSharedData from "../../hooks/useSharedData";

function FooterBase({
	logoImage,
	footerImageOne,
	footerImageTwo,
	termOfUseLink,
	privacyLink,
	securityLink,
	disclaimerLink,
	showQuoteSummaryDisclaimer = false,
	showPostQuoteDisclaimer = false,
}) {
	const theme = useTheme();
	const { pdsUrl } = useSharedData();

	return (
		<>
			<MainLayout>
				<Section
					sx={{ borderTop: `1px solid ${theme.palette.greyscale.Grey550}` }}
					mt={5}
					mb={0}
				></Section>
				<Section
					sx={{
						maxWidth: { xs: 140 },
						display: "flex",
						justifyContent: "center",
					}}
					mt={{ xs: 5, md: 8 }}
					mb={3}
				>
					<img width="100%" src={logoImage}></img>
				</Section>
				<Section mb={3} mt={0}>
					<Typography variant="label-large-bold-grey550">
						Industry Awards and Code of Practice
					</Typography>
				</Section>
				<Section
					sx={{ display: "flex", justifyContent: "center" }}
					mt={0}
					mb={3}
				>
					<Section sx={{ maxWidth: { xs: 90 } }} mt={0} mb={0}>
						<img width="100%" src={footerImageOne}></img>
					</Section>
					<Section sx={{ maxWidth: { xs: 90 } }} mt={0} mb={0} ml={5}>
						<img width="100%" src={footerImageTwo}></img>
					</Section>
				</Section>
			</MainLayout>
			<Section
				sx={{ backgroundColor: theme.palette.greyscale.Grey600 }}
				pt={5}
				pb={5}
				px={{ xs: 1, sm: 0 }}
				mt={0}
				mb={0}
			>
				<Section mt={0} mb={0}>
					{showQuoteSummaryDisclaimer && (
						<Section sx={{ maxWidth: { xs: 500, md: 1200 } }} ml={5}>
							<Typography variant="f1">
								This is a provisional quote based on our standard premium rates
								for your age, smoking status and gender. The actual premium
								applicable may be higher when we assess your individual
								circumstances. Refer to the{" "}
								<Link href={pdsUrl} target="_blank">
									Allianz Life Plan Product Disclosure Statement and Policy
									Document
								</Link>{" "}
								for details of how premiums are calculated. Depending on what
								you tell us, we may offer cover on different terms to those in
								this quote or decline cover. Eligibility criteria, standard
								terms, conditions, limits, definitions, exclusions, waiting
								periods, and exclusion periods apply. We provide you with
								factual information only about this life insurance cover. We
								don't provide any general or personal advice that's intended to
								influence you in deciding about this product or life insurance
								in general. We don't take into account your personal
								circumstances including: your objectives, financial situation or
								needs, or any information relating to your circumstances that we
								may hold or you may provide us. Before making a decision about
								our insurance, consider the Product Disclosure Statement (PDS).
								The PDS and Target Market Determination are available online
								from the{" "}
								<Link href={URLS.ALLIANZ_WEBSITE_URL} target="_blank">
									Allianz Insurance website
								</Link>{" "}
								or by calling{" "}
								<Link href={URLS.CONTACT_US_PHONE_URL}>
									{URLS.CONTACT_US_PHONE_NUMBER}
								</Link>
								. If you're unsure, seek advice from a financial adviser before
								deciding on appropriate insurance cover. Insurance is issued by
								Allianz Australia Life Insurance Limited ABN 27 076 033 782 AFS
								License No. 296559 (Allianz Life). 'We', 'us', and 'our' are
								references to Allianz Life.
							</Typography>
						</Section>
					)}

					{showPostQuoteDisclaimer && (
						<Section sx={{ maxWidth: { xs: 500, md: 1200 } }} ml={5}>
							<Typography variant="f1">
								Eligibility criteria, standard terms, conditions, limits,
								definitions, exclusions, waiting periods, and exclusion periods
								apply. We provide you with factual information only about this
								life insurance cover. We don't provide any general or personal
								advice that's intended to influence you in deciding about this
								product or life insurance in general. We don't take into account
								your personal circumstances including your objectives, financial
								situation or needs, or any information relating to your
								circumstances that we may hold or you may provide us. Before
								making a decision about our insurance, consider the{" "}
								<Link href={pdsUrl} target="_blank">
									Allianz Life Plan Product Disclosure Statement and Policy
									Document (PDS)
								</Link>
								. The PDS and Target Market Determination are available online
								from the{" "}
								<Link href={URLS.ALLIANZ_WEBSITE_URL} target="_blank">
									Allianz Australia website
								</Link>{" "}
								or by calling{" "}
								<Link href={URLS.CONTACT_US_PHONE_URL}>
									{URLS.CONTACT_US_PHONE_NUMBER}
								</Link>
								. If you're unsure, seek advice from a financial adviser before
								deciding on appropriate insurance cover. Insurance is issued by
								Allianz Australia Life Insurance Limited ABN 27 076 033 782 AFS
								License No. 296559 (Allianz Life). 'We', 'us', and 'our' are
								references to Allianz Life.
							</Typography>
						</Section>
					)}

					<Section>
						<Typography variant="f1">
							Allianz Australia Life Insurance Limited ABN 27 076 033 782
						</Typography>
					</Section>
					<Section mt={3}>
						<Typography variant="f2">AFS Licence No. 296559</Typography>
					</Section>
					<Section sx={{ display: "flex", justifyContent: "center" }} mt={3}>
						<Stack
							direction="row"
							divider={<Divider orientation="vertical" flexItem />}
							spacing={{ xs: 1 / 2, sm: 2 }}
						>
							<Link variant="f1" href={termOfUseLink} target="_blank">
								Terms of use
							</Link>
							<Link variant="f1" href={privacyLink} target="_blank">
								Privacy
							</Link>
							<Link variant="f1" href={securityLink} target="_blank">
								Security
							</Link>
							<Link variant="f1" href={disclaimerLink} target="_blank">
								Disclaimer
							</Link>
						</Stack>
					</Section>
				</Section>
			</Section>
		</>
	);
}

FooterBase.propTypes = {
	logoImage: PropTypes.string.isRequired,
	footerImageOne: PropTypes.string.isRequired,
	footerImageTwo: PropTypes.string.isRequired,
	termOfUseLink: PropTypes.string.isRequired,
	privacyLink: PropTypes.string.isRequired,
	securityLink: PropTypes.string.isRequired,
	disclaimerLink: PropTypes.string.isRequired,
};

FooterBase.defaultProps = {
	logoImage:
		"https://www.allianz.com/content/dam/onemarketing/system/allianz-logo.svg",
	footerImageOne:
		"https://www.allianzretireplus.com.au/content/dam/onemarketing/azau/allianz-life-dam/footer-image/life-customer.jpg",
	footerImageTwo:
		"https://www.allianzretireplus.com.au/content/dam/onemarketing/azau/allianz-life-dam/footer-image/life-ins-code.jpg",
	termOfUseLink: "https://www.allianz.com.au/terms-of-use.html",
	privacyLink: "https://www.allianz.com.au/privacy-policy.html",
	securityLink: "https://www.allianz.com.au/security.html",
	disclaimerLink: "https://www.allianz.com.au/disclaimer.html",
};

export default FooterBase;
